import {evaluatePixelDensity} from '../pixelDensity';

const ATOMIC_HTML_ELEMENTS = ['img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
const CSS_CLASS_FORCE_PAGE_BREAK = 'force-page-break';
const DATA_ATTR_ORIGINAL_HEIGHT = 'data-original-height';
const DATA_ATTR_ORIGINAL_TRANSFORM = 'data-original-transform';
const PDF_PAGE_HEIGHT_PORTRAIT_IN = 11;
const PDF_PAGE_MARGIN_IN = 0.5;
const PDF_PAGE_WIDTH_PORTRAIT_IN = 8.5;

/**
 * Creates a CSS @page rule for PDF export with the specified orientation.
 */
export function createPdfCssPageStyle(mode: 'landscape' | 'portrait'): string {
  return mode === 'portrait'
    ? `@page {size: ${PDF_PAGE_WIDTH_PORTRAIT_IN}in ${PDF_PAGE_HEIGHT_PORTRAIT_IN}in; margin: ${PDF_PAGE_MARGIN_IN}in}`
    : `@page {size: ${PDF_PAGE_HEIGHT_PORTRAIT_IN}in ${PDF_PAGE_WIDTH_PORTRAIT_IN}in; margin: ${PDF_PAGE_MARGIN_IN}in}`;
}

/**
 * Calculates the printable page height in pixels for the given orientation.
 */
export function getPdfPageHeightPx(mode: 'landscape' | 'portrait'): number {
  const ppi = evaluatePixelDensity();

  const pageHeightIn =
    mode === 'portrait'
      ? PDF_PAGE_HEIGHT_PORTRAIT_IN
      : PDF_PAGE_WIDTH_PORTRAIT_IN;

  return Math.ceil((pageHeightIn - 2 * PDF_PAGE_MARGIN_IN) * ppi);
}

export interface ElementPositionData {
  element: HTMLElement;
  positionY: number;
  height: number;
  originalTransform: string;
  adjustment: number;
}

/**
 * Repositions panel grid elements via CSS transforms to avoid page breaks.
 */
export function adjustContainerElementsToAvoidPageBreaks(
  container: HTMLElement,
  elements: HTMLElement[],
  pageHeightPx: number,
  pageTopPx: number = 0,
  accumulatedOffsetPx: number = 0
) {
  if (!elements.length) {
    return;
  }

  const containerOriginalHeightPx = container.getBoundingClientRect().height;

  const positions = elements
    .map(el => captureElementPosition(el, pageTopPx, accumulatedOffsetPx))
    .sort((a, b) => a.positionY - b.positionY);

  let accumulatedMarginPx = 0;
  const adjustments: Array<ElementPositionData> = [];

  for (const data of positions) {
    const borderAdjustment = calculateBorderAdjustmentPx(data.element);
    const adjustedY = data.positionY + accumulatedMarginPx;
    const adjustedBottom = adjustedY + data.height;

    const topPage = Math.floor(adjustedY / pageHeightPx);
    const bottomPage = Math.floor(adjustedBottom / pageHeightPx);

    if (topPage !== bottomPage && data.height <= pageHeightPx) {
      const nextPageStart = (topPage + 1) * pageHeightPx;
      const distanceToPageBreak = Math.ceil(nextPageStart - adjustedY);

      if (distanceToPageBreak > 0) {
        accumulatedMarginPx += distanceToPageBreak + borderAdjustment;
      }
    }

    adjustments.push({
      ...data,
      adjustment: accumulatedMarginPx,
    });
  }

  applyPageBreakAdjustments(adjustments);

  // Container height must be adjusted since repositioned elements are out of flow.
  adjustContainerHeight(
    container,
    containerOriginalHeightPx,
    accumulatedMarginPx
  );
}

/**
 * calculateOffsetFromAtomicElements predicts the additional document height
 * that will be added to the page due to the browser repositioning "atomic"
 * elements to avoid page breaks.
 */
export function calculateOffsetFromAtomicElements(
  atomicElements: HTMLElement[],
  pageHeightPx: number,
  pageTopPx: number,
  initialOffset: number
): number {
  let accumulatedOffsetPx = initialOffset;
  let maxBottomPx = 0;

  for (const element of atomicElements) {
    const {height: heightPx, top: topPx} = element.getBoundingClientRect();

    const effectiveTopPx = Math.floor(topPx - pageTopPx + accumulatedOffsetPx);
    const effectiveBottomPx = Math.ceil(effectiveTopPx + heightPx);

    const topPage = Math.floor(effectiveTopPx / pageHeightPx);
    const bottomPage = Math.floor(effectiveBottomPx / pageHeightPx);

    if (topPage === bottomPage) {
      maxBottomPx = Math.max(maxBottomPx, effectiveBottomPx);
      continue;
    }

    // This element will span a page break; calculate the additional vertical
    // height the browser will add pushing it onto the next page.

    const topPageNext = (topPage + 1) * pageHeightPx;
    const adjustedBottomPx = topPageNext + heightPx;

    const maxBottomPxBeforeAdjustment = Math.max(
      maxBottomPx,
      effectiveBottomPx
    );

    if (adjustedBottomPx > maxBottomPxBeforeAdjustment) {
      accumulatedOffsetPx += adjustedBottomPx - maxBottomPxBeforeAdjustment;
    }

    maxBottomPx = Math.max(maxBottomPx, adjustedBottomPx);
  }

  return accumulatedOffsetPx;
}

export function cleanUpPageBreakAdjustments(): void {
  const elements: HTMLElement[] = Array.from(
    document.querySelectorAll(`.${CSS_CLASS_FORCE_PAGE_BREAK}`)
  );
  for (const element of elements) {
    if (element.classList.contains(CSS_CLASS_FORCE_PAGE_BREAK)) {
      element.classList.remove(CSS_CLASS_FORCE_PAGE_BREAK);
    }

    const originalTransform = element.getAttribute(
      DATA_ATTR_ORIGINAL_TRANSFORM
    );
    if (originalTransform) {
      element.style.transform = originalTransform;
      element.removeAttribute(DATA_ATTR_ORIGINAL_TRANSFORM);
    }
  }

  const containers: HTMLElement[] = Array.from(
    document.querySelectorAll(`[${DATA_ATTR_ORIGINAL_HEIGHT}]`)
  );
  for (const element of containers) {
    const originalHeight = element.getAttribute(DATA_ATTR_ORIGINAL_HEIGHT);
    if (originalHeight !== null) {
      element.style.height = originalHeight;
      element.removeAttribute(DATA_ATTR_ORIGINAL_HEIGHT);
    }
  }
}

/**
 * findAtomicElements returns a document-ordered list of HTML elements within
 * 'root' that are "atomic" and won't be split across page breaks. The browser
 * will push these elements to the next page if they'd otherwise span a page
 * break.
 */
export function findAtomicElements(root: HTMLElement): HTMLElement[] {
  if (isAtomicElement(root)) {
    return [root];
  }
  return Array.from(root.children)
    .filter((c): c is HTMLElement => c instanceof HTMLElement)
    .flatMap(findAtomicElements);
}

/**
 * isAtomicElement returns whether the specified HTML element is "atomic",
 * meaning it will not be split across a page break and the browser will instead
 * reposition it at the start of the next page.
 */
export function isAtomicElement(element: HTMLElement): boolean {
  const tagName = element.tagName.toLowerCase();
  if (ATOMIC_HTML_ELEMENTS.includes(tagName)) {
    return true;
  }

  const style = window.getComputedStyle(element);
  if (style.breakInside === 'avoid' || style.pageBreakInside === 'avoid') {
    return true;
  }

  return false;
}

function adjustContainerHeight(
  container: HTMLElement,
  originalHeight: number,
  totalAdjustment: number
): void {
  if (totalAdjustment <= 0) {
    return;
  }

  if (!container.getAttribute(DATA_ATTR_ORIGINAL_HEIGHT)) {
    const currentHeight = container.style.height || '';
    container.setAttribute(DATA_ATTR_ORIGINAL_HEIGHT, currentHeight);
  }

  const newHeight = originalHeight + totalAdjustment;
  container.style.height = `${newHeight}px`;
}

function applyPageBreakAdjustments(
  elementDataList: ElementPositionData[]
): void {
  elementDataList.forEach(data => {
    if (data.adjustment <= 0) {
      return;
    }

    data.element.classList.add(CSS_CLASS_FORCE_PAGE_BREAK);
    data.element.setAttribute(
      DATA_ATTR_ORIGINAL_TRANSFORM,
      data.originalTransform
    );

    const adjustedTransform =
      data.originalTransform === 'none'
        ? `translateY(${data.adjustment}px)`
        : `${data.originalTransform} translateY(${data.adjustment}px)`;

    data.element.style.transform = adjustedTransform;
  });
}

function calculateBorderAdjustmentPx(element: HTMLElement): number {
  const computedStyle = window.getComputedStyle(element);
  return computedStyle.boxSizing === 'content-box'
    ? parseFloat(computedStyle.borderTopWidth) || 0
    : 0;
}

function captureElementPosition(
  element: HTMLElement,
  reportPageTop: number,
  accumulatedOffsetPx: number = 0
): Omit<ElementPositionData, 'adjustment'> {
  const rect = element.getBoundingClientRect();
  const computedStyle = window.getComputedStyle(element);

  return {
    element,
    positionY: rect.top - reportPageTop + accumulatedOffsetPx,
    height: rect.height,
    originalTransform: computedStyle.transform,
  };
}
